import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { z } from "zod";
import { useCallback, useState } from "react";
import { Button } from "@design-system/Button/Button";
import { CloseIcon } from "@icons";
import styles from "./OverPageCTA.module.scss";

export interface OverPageCTAProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    cta?: React.ReactNode;
    dismissKey?: string;
}

const stringArraySchema = z.array(z.string());

export const OverPageCTA: React.FC<OverPageCTAProps> = ({
    title,
    subtitle,
    cta,
    dismissKey,
}) => {
    const [dismissed, setDismissed] = useWorkspaceUserCache(
        WORKSPACE_USER_CACHE_KEYS.DISMISSED_CTAS,
        stringArraySchema,
    );

    const [isDismissed, setIsDismissed] = useState(
        dismissKey && dismissed?.includes(dismissKey),
    );

    const dismiss = useCallback(() => {
        if (!dismissKey) {
            return;
        }

        const newDismissed = [...(dismissed ?? []), dismissKey];
        setDismissed(newDismissed);
        setIsDismissed(true);
    }, [dismissKey, dismissed, setDismissed]);

    if (isDismissed) {
        return null;
    }

    return (
        <div className={styles.overPageCtaContainer}>
            <div className={styles.leftContent}>
                <div className={styles.title}>{title}</div>
                <div className={styles.subtitle}>{subtitle}</div>
            </div>
            <div className={styles.rightContent}>
                {cta}
                {dismissKey && (
                    <Button
                        iconOnly
                        onClick={dismiss}
                        variant="secondary"
                        size="sm"
                        aria-label="Dismiss"
                    >
                        <CloseIcon />
                    </Button>
                )}
            </div>
        </div>
    );
};
